/**
 * The handful of pages that need a real address of their own.
 *
 * Everything else in FilmSloth is a step in a live session — a lobby, a deck,
 * a result — and a URL for those would promise something the app cannot keep:
 * opening it later lands in a session that has long since ended. So the app
 * stays a single screen at `/` and only the standing pages get paths.
 *
 * They need them for two reasons. A crawler cannot press a button: a page that
 * exists only as React state is a page Google, AdSense's reviewer and anyone
 * pasting a link cannot reach. And "where do I find your imprint?" should be
 * answerable with an address rather than with directions.
 */
export const CONTENT_VIEWS = ['about', 'contact', 'privacy', 'impressum'] as const;

export type ContentView = (typeof CONTENT_VIEWS)[number];

/**
 * Paths are English like the rest of the code, with one deliberate exception:
 * `/impressum` is the address Germans actually look for, and the page exists
 * because German law asks for it.
 */
export const CONTENT_PATHS: Record<ContentView, string> = {
  about: '/about',
  contact: '/contact',
  privacy: '/privacy',
  impressum: '/impressum',
};

/** The canonical origin, used for `<link rel="canonical">` and the sitemap. */
export const SITE_ORIGIN = 'https://filmsloth.com';

export function isContentView(value: string): value is ContentView {
  return (CONTENT_VIEWS as readonly string[]).includes(value);
}

/**
 * The page a path stands for, or `null` for anything else — including `/`,
 * which is the app itself and not a content page.
 *
 * A trailing slash is accepted because people type one and because a link
 * shared with one should not land on the app instead of the page it named.
 */
export function viewForPath(pathname: string): ContentView | null {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  for (const view of CONTENT_VIEWS) {
    if (CONTENT_PATHS[view] === normalized) return view;
  }
  return null;
}

/** The path a page lives at; the app itself is `/`. */
export function pathForView(view: string): string {
  return isContentView(view) ? CONTENT_PATHS[view] : '/';
}

/** The absolute address of a page, for canonical links and shared URLs. */
export function canonicalUrl(view: string): string {
  const path = pathForView(view);
  return path === '/' ? `${SITE_ORIGIN}/` : SITE_ORIGIN + path;
}
